import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useToggler } from '@cbhq/cds-common';

import { useGetBuildQuestQuestions } from ':cloud/queries/BuildQuestQueries/useGetBuildQuestQuestions';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { LEVELS } from ':cloud/widgets/buildQuest/constants';
import { LoadingSpinner } from ':cloud/widgets/sharedcomponents/LoadingSpinner';

interface BuildQuestContextProps {
  totalPointsAwarded: number;
  currentLevelColor: string;
  playerLevel: number;
  setPlayerLevel: (level: number) => void;
  displayedPoints: number;
  setDisplayedPoints: (points: number) => void;
  isPrizesModalVisible: boolean;
  togglePrizesModalOn: () => void;
  togglePrizesModalOff: () => void;
}

const BuildQuestContext = createContext<BuildQuestContextProps | undefined>(undefined);

function getPlayerLevel(totalPointsAwarded: number): number {
  const sortedLevels = Object.keys(LEVELS).sort((a, b) => LEVELS[a].points - LEVELS[b].points);

  return Number(
    sortedLevels.reverse().find((level) => totalPointsAwarded >= LEVELS[level].points) || 1,
  );
}

type BuildQuestProviderProps = {
  children?: React.ReactNode;
};

export function BuildQuestProvider({ children }: BuildQuestProviderProps) {
  const { isLoading: isLoadingUser, user } = useGetUser();
  const { isLoading: isLoadingItems, items } = useGetBuildQuestQuestions(user.userId);

  // Points n stuff
  const totalPointsAwarded = useMemo(() => {
    return items.reduce((acc, item) => acc + item.pointsAwarded, 10);
  }, [items]);
  const [displayedPoints, setDisplayedPoints] = useState<number>(0);

  // Set displayedPoints once after items are loaded. After that, we only want to set this explicitly (for animation reasons)
  useEffect(() => {
    if (!isLoadingItems && items.length > 0 && displayedPoints === 0) {
      setDisplayedPoints(totalPointsAwarded);
    }
  }, [isLoadingItems, items, totalPointsAwarded, displayedPoints]);

  // Player level related things
  const [playerLevel, setPlayerLevel] = useState<number>(1);
  useEffect(() => {
    setPlayerLevel(getPlayerLevel(totalPointsAwarded));
  }, [totalPointsAwarded]);

  const currentLevelColor = useMemo((): string => {
    return LEVELS[playerLevel].color as string;
  }, [playerLevel]);

  // Modal togglers
  const [isPrizesModalVisible, { toggleOn: togglePrizesModalOn, toggleOff: togglePrizesModalOff }] =
    useToggler();

  const contextValue = useMemo(
    () => ({
      totalPointsAwarded,
      currentLevelColor,
      playerLevel,
      setPlayerLevel,
      displayedPoints,
      setDisplayedPoints,
      isPrizesModalVisible,
      togglePrizesModalOn,
      togglePrizesModalOff,
    }),
    [
      totalPointsAwarded,
      currentLevelColor,
      playerLevel,
      displayedPoints,
      isPrizesModalVisible,
      togglePrizesModalOn,
      togglePrizesModalOff,
    ],
  );

  if (isLoadingItems || isLoadingUser) {
    return <LoadingSpinner />;
  }

  return <BuildQuestContext.Provider value={contextValue}>{children}</BuildQuestContext.Provider>;
}

// Export the context for use in other components
export function useBuildQuestContext() {
  const context = useContext(BuildQuestContext);
  if (!context) {
    throw new Error('useBuildQuest must be used within a BuildQuestProvider');
  }
  return context;
}
